import { useMemo } from 'react';

import { observer } from 'mobx-react-lite';

import { Avatar } from '@components/avatar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { sortByObjectKey } from '@utils/collections';

import ShowMore from 'components/ShowMore';
import { useStore } from 'models/Provider';
import type { JobStatistics, JobStatisticsSourcingEmployees } from 'types/jobs';

import { SourceCard, Line } from './styles';

type Props = {
  statistics: JobStatistics;
};

export const EmployeeSources = observer<Props>(function EmployeeSources({ statistics }) {
  const { t } = useStore();

  const sources = useMemo(() => {
    return sortByObjectKey(
      Object.keys(statistics.sourcingEmployees).map((key) => ({
        ...statistics.sourcingEmployees[key],
      })),
      'count',
      true
    );
  }, [statistics.sourcingEmployees]);

  return (
    <Stack space={2}>
      <Text font="semibold14" color="purple100" text={t('job.statistics.sourcing.heading')} />
      <Stack space={2}>
        <ShowMore
          max={sources.length}
          count={3}
          inc={sources.length}
          render={({ count }: { count: number }) =>
            sources.slice(0, count).map((source) => {
              return (
                <Source key={source.id} source={source} total={statistics.counts.applicants} />
              );
            })
          }
        />
        {sources.length === 0 && (
          <Text text={t('job.statistics.sourcing.empty')} font="regular16" color="purple60" />
        )}
      </Stack>
    </Stack>
  );
});

function Source({
  source,
  total,
}: {
  source: JobStatisticsSourcingEmployees[number];
  total: number;
}) {
  const { t, userStore } = useStore();
  const user = userStore.items.get(source.id);
  const { count, hired } = source;
  if (!user) return null;

  return (
    <SourceCard>
      <Stack orientation="horizontal" space={6} justify="space-between" align="center">
        <Stack orientation="horizontal" space={4} align="center">
          <Avatar name={user.name} src={user.picture} size="md" />
          <Text font="bold16" color="purple200" text={user.name} />
        </Stack>
        <Stack orientation="horizontal" space={6} align="center">
          <Stack space={0} align="center">
            <Stack orientation="horizontal" space={2} justify="center" style={{ width: '6.5rem' }}>
              <Text font="heading2" color="purple100" text={count} />
              <Text font="heading2" color="purple40" text="/" />
              <Text
                font="heading2"
                color="blue100"
                text={count === 0 || total === 0 ? '0%' : `${((count / total) * 100).toFixed(0)}%`}
              />
            </Stack>
            <Text font="regular12" color="purple80" text={t('job.statistics.applicants')} />
          </Stack>
          <Line />
          <Stack space={0} align="center">
            <Stack orientation="horizontal" space={2} justify="center" style={{ width: '6.5rem' }}>
              <Text font="heading2" color="purple100" text={hired} />
              <Text font="heading2" color="purple40" text="/" />
              <Text
                font="heading2"
                color="blue100"
                text={hired === 0 || total === 0 ? '0%' : `${((hired / total) * 100).toFixed(0)}%`}
              />
            </Stack>
            <Text font="regular12" color="purple80" text={t('job.statistics.hired')} />
          </Stack>
        </Stack>
      </Stack>
    </SourceCard>
  );
}
